import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { RichTextDisplay } from '@bonusly/common-rich-text-editor';
import { Box, BoxProps, Button, Flex, Image, rem, Text, useDisclosure } from '@bonusly/design-system';

import { AnnouncementHeader } from '.';
import { useClaimIncentiveModal } from '../../../../modules/incentives';
import { Announcement } from '../../types/announcements';
import { stripEmojis } from '../../utils/text';
import { ClaimIncentiveModal } from '../claim-incentive-modal';
import { ImageModal } from '../image-modal';

export interface AnnouncementCardProps extends BoxProps {
	announcement: Announcement;
	collapsed?: boolean;
	onClick?: () => void;
	fontSize?: 'sm' | 'lg';
}

export const AnnouncementCard: FC<AnnouncementCardProps> = ({ announcement, collapsed = false, onClick, fontSize = 'lg', ...boxProps }) => {
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const announcementImageModal = useDisclosure();
	const incentiveModal = useDisclosure();
	const claimIncentiveModal = useClaimIncentiveModal();

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			track(AnalyticsEvent.Announcements.AnnouncementClaimIncentiveButtonClicked);
			claimIncentiveModal.onOpen({ incentiveId: announcement.claimable_award_id });
		},
		[announcement, claimIncentiveModal, track]
	);

	const handleImageClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			announcementImageModal.onOpen();
		},
		[announcementImageModal]
	);

	return (
		<Box
			as={collapsed ? 'button' : 'div'}
			aria-label={collapsed ? t('viewAnnouncement', { title: announcement.title }) : undefined}
			bg="bg.surface"
			p={collapsed ? 3 : 4}
			borderRadius="sm"
			mx="auto"
			width="100%"
			borderColor="bg.soft"
			textAlign="left"
			{...boxProps}
			onClick={onClick}
			cursor={collapsed ? 'pointer' : 'default'}
			data-testid="announcement-card"
		>
			{!collapsed && <AnnouncementHeader createdById={announcement.created_by_id} startDate={announcement.start_date} />}

			<Flex>
				<Box w="100%">
					<Text fontSize={fontSize === 'sm' ? 16 : 24} fontWeight="semibold">
						{collapsed ? stripEmojis(announcement.title) : announcement.title}
					</Text>

					{collapsed ? (
						<Box noOfLines={1} textOverflow="ellipsis" fontSize={14}>
							<RichTextDisplay>{announcement.description}</RichTextDisplay>
						</Box>
					) : (
						<Box fontSize={fontSize === 'sm' ? 14 : 16}>
							<RichTextDisplay>{announcement.description}</RichTextDisplay>
						</Box>
					)}

					{announcement.claimable_award_id && (
						<>
							{collapsed ? (
								<Text as="span" color="primary.fg" fontSize="sm" fontWeight="medium">
									{t('carousel.incentive_button')}
								</Text>
							) : (
								<Button
									colorScheme="primary"
									data-testid="incentive-button"
									mt={2}
									onClick={handleClick}
									size={fontSize === 'sm' ? 'xs' : 'sm'}
									variant="solid"
									_hover={{ textDecoration: 'none' }}
									width="100%"
									justifyContent="center"
								>
									{t('carousel.incentive_button')}
								</Button>
							)}
							<ClaimIncentiveModal incentiveId={announcement.claimable_award_id} isOpen={incentiveModal.isOpen} onClose={incentiveModal.onClose} />
						</>
					)}
				</Box>
				{announcement.image?.url && (
					<Box w={rem(88)} ml={1}>
						<Image borderRadius="sm" cursor="pointer" data-testid="announcement-image" onClick={handleImageClick} src={announcement.image.url} />
					</Box>
				)}
			</Flex>
			<ImageModal imageUrl={announcement.image?.url} isOpen={announcementImageModal.isOpen} onClose={announcementImageModal.onClose} />
		</Box>
	);
};
